import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import BottomLoadingIndicator from "components/common/BottomLoadingIndicator";
import { Family, FamilyStatus } from "protogen/advisors_service_pb";
import { Member } from "protogen/common_pb";
import AddFamilyDialog from "components/family/AddFamilyDialog";
import {
  useListAllFamilies,
  useListFamilyDirectoryMapLocations,
} from "services/admin";
import {
  FamilyDirectoryMapLocation,
  FamilyDirectoryMapLocationType,
  FamilyDirectoryMarket,
  ListAllFamiliesResponse_FamilyPermissions,
} from "protogen/admin_service_pb";
import LinkRouter from "components/navigation/LinkRouter";
import useIsMobile from "components/hooks/useIsMobile";
import Breadcrumbs from "components/common/Breadcrumbs";
import { Map as MapIcon, Plus, Settings } from "lucide-react";

import { commaSeparatedEnglishList } from "../../common/utils";
import CheckedDropDown from "components/common/CheckedDropDown";
import SearchCheckedDropDown, {
  Option,
} from "components/common/SearchCheckedDropDown";
import { AccountStub } from "protogen/common_pb";
import { useNavigate } from "react-router";
import { getFormattedLocation } from "components/family/utils";
import MapList from "components/common/maps/MapList";
import { Treatment } from "components/common/maps/FMarker";
import SearchBar from "components/common/SearchBar";
import { useSearchParams } from "react-router-dom";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList as List, ListChildComponentProps } from "react-window";
const getStateColor = (status: FamilyStatus) => {
  switch (status) {
    case FamilyStatus.ACTIVE:
      return "#198282";
    case FamilyStatus.PROSPECT:
      return "#9A6518";
    case FamilyStatus.DEACTIVATED:
    case FamilyStatus.DIGITAL_DEACTIVATED:
    case FamilyStatus.DIGITAL_DEACTIVATED_TRIAL:
      return "#D32F2F";
    case FamilyStatus.DIGITAL_SUBSCRIPTION:
    case FamilyStatus.DEMO:
    case FamilyStatus.TEST:
    default:
      return "#8E9598";
  }
};

const getStatusLabel = (status: FamilyStatus) => {
  switch (status) {
    case FamilyStatus.ACTIVE:
      return "Active";
    case FamilyStatus.PROSPECT:
      return "Prospect";
    case FamilyStatus.DEMO:
      return "Demo";
    case FamilyStatus.TEST:
      return "Test";
    case FamilyStatus.DEACTIVATED:
      return "Deactivated";
    case FamilyStatus.DIGITAL_DEACTIVATED:
      return "Digital Deactivated";
    case FamilyStatus.DIGITAL_DEACTIVATED_TRIAL:
      return "Digital Trial Ended";
    case FamilyStatus.DEACTIVATED_PROSPECT:
      return "Deactivated Prospect";
    case FamilyStatus.PREACTIVATION:
      return "Pre-Activation";
    case FamilyStatus.UNPAID_SIGNUP:
      return "Free Signup";
    case FamilyStatus.DIGITAL_SUBSCRIPTION:
      return "Digital Subscription";
    default:
      return "Unknown";
  }
};

type FamilyRowProps = {
  family: Family;
  advisor: AccountStub | null;
};

const FamilyCard = ({ family, advisor }: FamilyRowProps) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const onEdit = () => {
    navigate(
      `/families/${encodeURIComponent(family.ref)}?adminView=1#tab-admin`,
    );
  };
  const byline = (
    <Box>
      {advisor && (
        <Typography
          variant="body"
          onClick={() =>
            navigate(
              `/tools/advisor-administration/${encodeURIComponent(
                advisor.ref,
              )}`,
            )
          }
        >
          {advisor.displayName}
        </Typography>
      )}
    </Box>
  );
  const location = `${getFormattedLocation(family.address)}`;
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="8px"
      sx={{
        borderRadius: "8px",
        border: "1px solid #ECECEC",
        background: "#FFF",
        width: "100%",
        padding: "16px 24px",
        gap: "8px",
      }}
    >
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap="12px"
      >
        <Box flexGrow={1}>
          <LinkRouter
            to={`/families/${encodeURIComponent(
              family.ref,
            )}?adminView=1#tab-admin`}
          >
            <Box display="flex" flexDirection={"column"} gap="4px">
              <Box
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                gap="4px"
              >
                <Typography
                  variant="bodyHeavy"
                  color="#262626"
                  sx={{
                    textWrap: "nowrap",
                  }}
                >
                  {family.name}
                </Typography>
                {!isMobile && <> &middot; </>}
                <Typography
                  sx={{
                    ...(!isMobile && {
                      textWrap: "nowrap",
                      textOverflow: "ellipsis",
                      overflowX: "hidden",
                    }),
                  }}
                  color="text.secondary"
                >
                  {commaSeparatedEnglishList(
                    family.familyMembers.map((m: Member) => m.displayName),
                  )}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Typography
                  variant="body"
                  color="text.secondary"
                  sx={{
                    textWrap: "none",
                    textOverflow: "ellipses",
                    overflow: "hidden",
                  }}
                >
                  {isMobile ? location : byline}
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #EAEBEC",
                    color: getStateColor(family.status),
                    borderRadius: "12px",
                    padding: "2px 8px",
                    fontSize: "11px",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    height: "fit-content",
                    alignSelf: isMobile ? "flex-start" : "center",
                  }}
                >
                  {getStatusLabel(family.status)}
                </Box>
              </Box>
            </Box>
          </LinkRouter>
        </Box>
        <Box display="flex" gap="32px" alignItems="center" justifyContent="end">
          {!isMobile && (
            <Typography
              variant="body"
              color="#262626"
              sx={{
                textAlign: "right",
              }}
            >
              {location}
            </Typography>
          )}
          <Box display="flex" flexDirection="column">
            <IconButton onClick={onEdit}>
              <Settings size={24} color="#198282" />
            </IconButton>
          </Box>
        </Box>
      </Box>
      {isMobile && byline && (
        <Typography variant="bodySmall" color="#6B6E7B">
          {byline}
        </Typography>
      )}
    </Box>
  );
};

const statusOptions = () => {
  return [
    {
      label: "Any status",
      value: "",
      mutuallyExclusive: true,
    },
    {
      label: "Active",
      value: FamilyStatus.ACTIVE.toString(),
      mutuallyExclusive: true,
    },
    {
      label: "Prospect",
      value: FamilyStatus.PROSPECT.toString(),
      mutuallyExclusive: true,
    },
    {
      label: "Demo",
      value: FamilyStatus.DEMO.toString(),
      mutuallyExclusive: true,
    },
    {
      label: "Test",
      value: FamilyStatus.TEST.toString(),
      mutuallyExclusive: true,
    },
    {
      label: "Deactivated",
      value: FamilyStatus.DEACTIVATED.toString(),
      mutuallyExclusive: true,
    },
    {
      label: "Digital Deactivated",
      value: FamilyStatus.DIGITAL_DEACTIVATED.toString(),
      mutuallyExclusive: true,
    },
    {
      label: "Digital Trial Ended",
      value: FamilyStatus.DIGITAL_DEACTIVATED_TRIAL.toString(),
      mutuallyExclusive: true,
    },
    {
      label: "Deactivated Prospect",
      value: FamilyStatus.DEACTIVATED_PROSPECT.toString(),
      mutuallyExclusive: true,
    },
    {
      label: "Pre-Activation",
      value: FamilyStatus.PREACTIVATION.toString(),
      mutuallyExclusive: true,
    },
    {
      label: "Free Signup",
      value: FamilyStatus.UNPAID_SIGNUP.toString(),
      mutuallyExclusive: true,
    },
    {
      label: "Digital Subscription",
      value: FamilyStatus.DIGITAL_SUBSCRIPTION.toString(),
      mutuallyExclusive: true,
    },
  ];
};

const locationOptions = (markets: FamilyDirectoryMarket[]) => {
  const options = markets.map((market) => ({
    label: market.name,
    value: market.code.toString(),
    mutuallyExclusive: true,
  }));
  options.unshift({
    label: "Any market",
    value: "",
    mutuallyExclusive: true,
  });
  return options;
};

const createAdvisorOptions = (advisors: AccountStub[]): Option[] => {
  const options = [...advisors]
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
    .map((advisor: AccountStub) => ({
      label: advisor.displayName,
      value: advisor.ref,
      mutuallyExclusive: true,
    }));
  options.unshift({
    label: "Any advisor",
    value: "",
    mutuallyExclusive: true,
  });
  return options;
};

type PersistedFilters = {
  status: string;
  setStatus: (s: string) => void;
  advisor: string;
  setAdvisor: (s: string) => void;
  location: string;
  setLocation: (s: string) => void;
  query: string;
  setQuery: (s: string) => void;
};
const usePersistedFilters = ({}: {}): PersistedFilters => {
  const initialParams = new URLSearchParams(window.location.search);
  const [, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<string>(
    initialParams.get("status") || "",
  );
  const [advisor, setAdvisor] = useState<string>(
    initialParams.get("advisor") || "",
  );
  const [location, setLocation] = useState<string>(
    initialParams.get("location") || "",
  );
  const [query, setQuery] = useState<string>(initialParams.get("query") || "");
  useEffect(() => {
    setSearchParams({
      ...(status ? { status } : {}),
      ...(advisor ? { advisor } : {}),
      ...(location ? { location } : {}),
      ...(query ? { query } : {}),
    });
  }, [status, advisor, location, query]);
  return {
    status,
    setStatus,
    advisor,
    setAdvisor,
    location,
    setLocation,
    query,
    setQuery,
  };
};

const FamilyFilters = ({
  advisors,
  markets,
  extra = null,
  filters,
}: {
  advisors: AccountStub[];
  markets: FamilyDirectoryMarket[];
  extra?: ReactNode | ReactNode[];
  filters: PersistedFilters;
}) => {
  const isMobile = useIsMobile();
  const dmaOptions = locationOptions(markets);
  const advisorOptions = createAdvisorOptions(advisors);
  return (
    <Box
      display={"flex"}
      flexDirection="row"
      sx={{ overflowX: "auto", flexShrink: 0 }}
      justifyContent={!isMobile ? "end" : undefined}
    >
      <Box
        display={"flex"}
        flexDirection="row"
        justifyContent="end"
        gap="10px"
        mb={isMobile ? "20px" : undefined}
      >
        <CheckedDropDown
          disabled={false}
          radio={true}
          initial={[filters.status]}
          options={statusOptions()}
          sx={{ width: isMobile ? "50%" : "fit-content", minWidth: "120px" }}
          maxDropdownHeight={"343px"}
          displayValue={
            (filters.status &&
              statusOptions().find((s) => s.value === filters.status)?.label) ||
            "Status"
          }
          onChange={(f) => f.length === 1 && filters.setStatus(f[0].value)}
        />
        <SearchCheckedDropDown
          disabled={false}
          placeholder="Advisor"
          options={advisorOptions}
          sx={{ width: isMobile ? "50%" : "fit-content", minWidth: "170px" }}
          value={
            advisorOptions.find((a) => a.value === filters.advisor) || null
          }
          onChange={(f) => filters.setAdvisor(f.value)}
        />
        <CheckedDropDown
          disabled={false}
          radio={true}
          initial={[filters.location]}
          options={dmaOptions}
          sx={{ width: isMobile ? "50%" : "fit-content", minWidth: "120px" }}
          maxDropdownHeight={"343px"}
          displayValue={
            (filters.location &&
              dmaOptions.find((l) => l.value === filters.location)?.label) ||
            "Market"
          }
          onChange={(f) => f.length === 1 && filters.setLocation(f[0].value)}
        />
        {extra}
      </Box>
    </Box>
  );
};

export default () => {
  const isMobile = useIsMobile();
  const { loading, request, cancel } = useListAllFamilies();
  const { request: mapRequest } = useListFamilyDirectoryMapLocations();
  const [mapOpen, setMapOpen] = useState(false);
  const [addFamilyModalOpen, setAddFamilyModalOpen] = useState(false);
  const [families, setFamilies] = useState<Family[]>([]);
  const [nextCursor, setNextCursor] = useState("");
  const [markets, setMarkets] = useState<FamilyDirectoryMarket[]>([]);
  const [primaryAdvisors, setPrimaryAdvisors] = useState<AccountStub[]>([]);
  const [mapLocations, setMapLocations] = useState<
    FamilyDirectoryMapLocation[]
  >([]);
  const [familyPermissions, setFamilyPermissions] = useState<
    Map<string, ListAllFamiliesResponse_FamilyPermissions>
  >(new Map());
  const pageSize = 50;
  const filters = usePersistedFilters({});
  const requestGeneration = useRef(0);
  const loadingMore = useRef(false);
  const nextCursorRef = useRef("");
  const requestedCursors = useRef(new Set<string>());

  const requestParams = (cursor: string) => ({
    limit: pageSize,
    cursor,
    status: filters.status
      ? (parseInt(filters.status) as FamilyStatus)
      : FamilyStatus.NOT_SET,
    advisorRef: filters.advisor,
    marketCode: /^\d+$/.test(filters.location) ? parseInt(filters.location) : 0,
    query: filters.query,
  });

  const mergePermissions = (
    permissions: ListAllFamiliesResponse_FamilyPermissions[],
    append: boolean,
  ) => {
    setFamilyPermissions((current) => {
      const updated = append ? new Map(current) : new Map();
      permissions.forEach((permission) => {
        updated.set(permission.familyRef, permission);
      });
      return updated;
    });
  };

  useEffect(() => {
    const generation = ++requestGeneration.current;
    loadingMore.current = false;
    nextCursorRef.current = "";
    requestedCursors.current.clear();
    setFamilies([]);
    setNextCursor("");
    setFamilyPermissions(new Map());
    setMapOpen(false);
    setMapLocations([]);

    const loadFirstPage = async () => {
      const response = await request(requestParams(""));
      if (!response || generation !== requestGeneration.current) return;
      nextCursorRef.current = response.nextCursor;
      setNextCursor(response.nextCursor);
      setFamilies(response.families);
      setMarkets(response.markets);
      setPrimaryAdvisors(response.primaryAdvisors);
      mergePermissions(response.familyPermissions, false);
    };
    loadFirstPage();
    return cancel;
  }, [filters.status, filters.advisor, filters.location, filters.query]);

  useEffect(() => {
    if (!filters.location || /^\d+$/.test(filters.location)) return;
    const legacyMarket = markets.find(
      (market) => market.name === filters.location,
    );
    if (legacyMarket) {
      filters.setLocation(legacyMarket.code.toString());
    }
  }, [markets, filters.location]);

  const loadNextPage = async () => {
    const cursor = nextCursorRef.current;
    if (!cursor || loadingMore.current || requestedCursors.current.has(cursor))
      return;

    requestedCursors.current.add(cursor);
    loadingMore.current = true;
    const generation = requestGeneration.current;
    try {
      const response = await request(requestParams(cursor));
      if (response && generation === requestGeneration.current) {
        nextCursorRef.current = response.nextCursor;
        setNextCursor(response.nextCursor);
        setFamilies((current) => {
          const knownRefs = new Set(current.map((family) => family.ref));
          return [
            ...current,
            ...response.families.filter((family) => !knownRefs.has(family.ref)),
          ];
        });
        mergePermissions(response.familyPermissions, true);
      }
    } finally {
      requestedCursors.current.delete(cursor);
      loadingMore.current = false;
    }
  };

  const openMap = async () => {
    const generation = requestGeneration.current;
    setMapLocations([]);
    setMapOpen(true);
    const response = await mapRequest({
      status: filters.status
        ? (parseInt(filters.status) as FamilyStatus)
        : FamilyStatus.NOT_SET,
      advisorRef: filters.advisor,
      marketCode: /^\d+$/.test(filters.location)
        ? parseInt(filters.location)
        : 0,
      query: filters.query,
    });
    if (response && generation === requestGeneration.current) {
      setMapLocations(response.locations);
    }
  };

  const mapListLocations = useMemo(
    () =>
      mapLocations.map((location) => {
        const isAdvisor =
          location.type === FamilyDirectoryMapLocationType.ADVISOR;
        return {
          title: location.title,
          link: isAdvisor
            ? `/advisor/${encodeURIComponent(location.ref)}`
            : `/families/${encodeURIComponent(
                location.ref,
              )}?adminView=1#tab-admin`,
          content: location.formattedAddress,
          color: isAdvisor ? "#165BAA" : getStateColor(location.familyStatus),
          treatment: isAdvisor ? Treatment.PersistentDot : undefined,
          bounding: !isAdvisor,
          address: location.formattedAddress,
          lat: location.latitude || undefined,
          lng: location.longitude || undefined,
        };
      }),
    [mapLocations],
  );

  const resultingFamilies = families;

  const Row = ({ index, style, data }: ListChildComponentProps) => {
    if (!data) return null;
    const family = data[index];
    return (
      <Box style={style}>
        <FamilyCard
          key={family.ref}
          family={family}
          advisor={familyPermissions.get(family.ref)?.primaryAdvisor}
        />
      </Box>
    );
  };
  return (
    <Box
      sx={{
        margin: isMobile ? "" : "auto",
        maxWidth: "1000px",
        padding: isMobile ? "20px 20px 0 20px" : "64px 64px 0 64px",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box display="flex" flexDirection="row">
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          marginBottom="16px"
        >
          <Breadcrumbs
            breadcrumbs={[
              {
                name: "Home",
                link: "/",
              },
              { name: "Tools", link: "/tools" },
            ]}
          />
          <Box
            display="flex"
            width="100%"
            gap={isMobile ? "12px" : "24px"}
            {...(isMobile && { flexDirection: "column" })}
            {...(!isMobile && {
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            })}
          >
            <Typography variant="display">Family Directory</Typography>
            <Box
              display="flex"
              flexDirection="row"
              gap="8px"
              alignItems={"center"}
            >
              <SearchBar
                initialQuery={filters.query}
                expand={false}
                autofocus={true}
                onQuery={filters.setQuery}
                onKeyPress={true}
                debounceTimeoutMs={300}
              />
              <Button
                variant="outlined"
                onClick={() => setAddFamilyModalOpen(true)}
                sx={{
                  height: "36px",
                  minWidth: "unset",
                  padding: "0 4px",
                }}
              >
                <Plus />
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      <FamilyFilters
        advisors={primaryAdvisors}
        markets={markets}
        filters={filters}
        extra={
          <Button
            onClick={openMap}
            sx={{
              borderRadius: "100px",
              border: "2px solid #EAEBEC",
              color: "#616161",
              backgroundColor: "unset",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#EAEBEC",
              },
            }}
            startIcon={<MapIcon size={20} stroke="#8E9598" />}
          >
            Mapped
          </Button>
        }
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: "16px",
          padding: !isMobile ? "24px 0" : undefined,
        }}
      >
        {!isMobile && (
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="end"
          >
            <Box
              display="flex"
              gap="32px"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="bodySmallHeavy" color="#262626">
                Name
              </Typography>
            </Box>
            <Box
              display="flex"
              gap="32px"
              alignItems="center"
              justifyContent="space-between"
              mr={"32px"}
            >
              <Typography
                variant="bodySmallHeavy"
                color="#262626"
                alignSelf={"end"}
                mr={"54px"}
              >
                Location
              </Typography>
              <Typography variant="bodySmallHeavy" color="#262626">
                Edit
              </Typography>
            </Box>
          </Box>
        )}
        <Box
          sx={{
            height: "100%",
            flex: "1 1 auto",
            position: "relative",
            ".hide-scrollbar::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <AutoSizer>
            {({ height, width }: { height: number; width: number }) => (
              <List
                height={height}
                itemCount={resultingFamilies.length}
                itemSize={isMobile ? 214 : 114}
                width={width}
                itemData={resultingFamilies}
                className="hide-scrollbar"
                onItemsRendered={({ visibleStopIndex }) => {
                  if (
                    nextCursor &&
                    visibleStopIndex >= resultingFamilies.length - 20
                  ) {
                    loadNextPage();
                  }
                }}
              >
                {Row}
              </List>
            )}
          </AutoSizer>
          {loading && <BottomLoadingIndicator label="Loading families…" />}
        </Box>
      </Box>
      <AddFamilyDialog
        selectAdvisor={true}
        open={addFamilyModalOpen}
        onClose={() => setAddFamilyModalOpen(false)}
      />
      <MapList
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        locations={mapListLocations}
      />
    </Box>
  );
};
